<template>
  <aside class="sidebar" :class="{ 'sidebar--collapsed': isCollapsed }">
    <div class="sidebar__header">
      <div class="sidebar__logo-mark">
        <ParkosMark :size="28" on-dark />
      </div>
      <span v-if="!isCollapsed" class="sidebar__logo-text">Parkos</span>
    </div>

    <nav class="sidebar__nav">
      <router-link
        v-for="item in visibleNavItems"
        :key="item.label"
        :to="item.to"
        class="sidebar__nav-item"
        :class="{ 'sidebar__nav-item--active': item.match && item.match(route.path) }"
        :active-class="item.match ? '' : 'sidebar__nav-item--active'"
        :title="isCollapsed ? item.label : undefined"
        :data-tour="`nav-${item.pageKey}`"
      >
        <span class="mdi sidebar__nav-icon" :class="item.icon"></span>
        <span v-if="!isCollapsed" class="sidebar__nav-label">{{ item.label }}</span>
      </router-link>

      <div class="sidebar__divider"></div>

      <router-link
        v-for="item in visibleAccountItems"
        :key="item.label"
        :to="item.to"
        class="sidebar__nav-item"
        active-class="sidebar__nav-item--active"
        :title="isCollapsed ? item.label : undefined"
        :data-tour="item.pageKey ? `nav-${item.pageKey}` : undefined"
      >
        <span class="mdi sidebar__nav-icon" :class="item.icon"></span>
        <span v-if="!isCollapsed" class="sidebar__nav-label">{{ item.label }}</span>
      </router-link>
    </nav>

    <button
      v-if="canSeeTour"
      type="button"
      class="sidebar__help"
      :title="isCollapsed ? 'Guía de Parkos' : undefined"
      @click="startTour()"
    >
      <span class="mdi mdi-compass-outline sidebar__nav-icon"></span>
      <span v-if="!isCollapsed" class="sidebar__nav-label">Guía de Parkos</span>
    </button>

    <div v-if="currentUser" class="sidebar__user" :title="isCollapsed ? currentUser.name || currentUser.email : undefined">
       <div v-if="!isCollapsed" class="sidebar__user-avatar" :style="{ background: currentUser.avatarColor || '#3b82f6' }">        <img v-if="currentUser.avatarUrl" :src="avatarSrc(currentUser.avatarUrl)" class="sidebar__user-avatar-img" alt="" />
        <template v-else>{{ userInitial }}</template>
      </div>
      <div v-if="!isCollapsed" class="sidebar__user-info">
        <span class="sidebar__user-name">{{ currentUser.name || currentUser.email }}</span>
        <button class="sidebar__user-logout" @click="onLogout">Cerrar sesión</button>
      </div>
      <button
        v-else
        class="sidebar__user-logout-icon"
        @click="onLogout"
        aria-label="Cerrar sesión"
      >
        <span class="mdi mdi-logout"></span>
      </button>
    </div>

    <button
      class="sidebar__toggle"
      @click="toggleCollapse"
      :aria-label="isCollapsed ? 'Expandir menú' : 'Colapsar menú'"
    >
      <span class="mdi" :class="isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left'"></span>
    </button>
  </aside>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/services/authStore'
import { userHasPageAccess } from '@/constants/accessControl'
import { avatarSrc } from '@/services/auth'
import { startTour } from '@/services/tour'
import ParkosMark from '@/components/Global/ParkosMark.vue'

const router = useRouter()
const route = useRoute()
const { state, logout } = useAuthStore()

const isCollapsed = ref(false)

const currentUser = computed(() => state.user)
const userInitial = computed(() => {
  const source = currentUser.value?.name || currentUser.value?.email || ''
  return source.trim().charAt(0).toUpperCase() || '?'
})

const navItems = [
  { label: 'Inicio', icon: 'mdi-view-dashboard-outline', to: '/inicio', pageKey: 'dashboard' },
  { label: 'Contratos', icon: 'mdi-file-document-edit-outline', to: '/contratos', pageKey: 'contratos' },
  { label: 'Mantenimiento', icon: 'mdi-wrench-outline', to: '/mantenimiento', pageKey: 'mantenimiento' },
  { label: 'Servicios', icon: 'mdi-fire-hydrant', to: '/servicios', pageKey: 'servicios' },
  { label: 'Cobranza', icon: 'mdi-cash-multiple', to: '/cobranza', pageKey: 'cobranza' },
  { label: 'Finanzas', icon: 'mdi-wallet-outline', to: '/finanzas', pageKey: 'finanzas' },
  // Portal del técnico: solo lo ve quien tiene rol Técnico (el administrador tiene el permiso pero no usa este menú).
  { label: 'Mis tickets', icon: 'mdi-clipboard-list-outline', to: '/tecnico', pageKey: 'portalTecnicos', onlyRole: 'TECHNICIAN', match: (p) => p === '/tecnico' || p.startsWith('/tecnico/ticket') },
  { label: 'Calendario', icon: 'mdi-calendar-month-outline', to: '/tecnico/calendario', pageKey: 'portalTecnicos', onlyRole: 'TECHNICIAN', match: (p) => p.startsWith('/tecnico/calendario') },
]

const accountItems = [
  { label: 'Configuracion', icon: 'mdi-cog-outline', to: '/configuracion', pageKey: 'configuracion' },
  { label: 'Cuenta', icon: 'mdi-account-circle-outline', to: '/cuenta', pageKey: null },
]

const visibleNavItems = computed(() =>
  navItems.filter(
    (item) =>
      (!item.onlyRole || item.onlyRole === currentUser.value?.role) && userHasPageAccess(currentUser.value, item.pageKey)
  )
)
// El técnico trabaja desde su portal y no ve la guía.
const canSeeTour = computed(() => !!currentUser.value && currentUser.value.role !== 'TECHNICIAN')
const visibleAccountItems = computed(() => accountItems.filter((item) => !item.pageKey || userHasPageAccess(currentUser.value, item.pageKey)))

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value
}

async function onLogout() {
  await logout()
  router.push('/login')
}
</script>

<style scoped>
.sidebar {
  display: flex;
  flex-direction: column;
  width: 240px;
  background: var(--side-panel);
  backdrop-filter: blur(12px);
  color: var(--text-primary);
  height: 100vh;
  flex-shrink: 0;
  transition: width 0.2s ease;
  overflow: hidden;
}

.sidebar--collapsed {
  width: 72px;
}

.sidebar__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  white-space: nowrap;
}

.sidebar__logo-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.4rem;
  line-height: 1;
}

.sidebar__logo-text {
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 400;
  letter-spacing: 0.01em;
}

.sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  flex: 1;
}

.sidebar__nav-item {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  color: var(--text-primary);
  text-decoration: none;
  font-family: var(--font-body);
  font-weight: 400;
  font-size: 0.9rem;
  white-space: nowrap;
  opacity: 0.85;
  transition: background 0.15s ease, opacity 0.15s ease;
}

.sidebar__nav-item:hover {
  background: rgba(255, 255, 255, 0.1);
  opacity: 1;
}

.sidebar__nav-item--active {
  background: rgba(255, 255, 255, 0.1);
  opacity: 1;
}

.sidebar__nav-icon {
  flex-shrink: 0;
  font-size: 1.15rem;
  line-height: 1;
  opacity: 0.9;
}

.sidebar--collapsed .sidebar__nav-item {
  justify-content: center;
  padding: 0.65rem;
}

/* Botón para volver a ver la guía de bienvenida. */
.sidebar__help {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin: 0 0.75rem 0.4rem;
  padding: 0.65rem 0.75rem;
  border: none;
  border-radius: 8px;
  background: none;
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: 0.9rem;
  white-space: nowrap;
  cursor: pointer;
  opacity: 0.7;
  transition: background 0.15s ease, opacity 0.15s ease;
}

.sidebar__help:hover {
  background: rgba(255, 255, 255, 0.1);
  opacity: 1;
}

.sidebar--collapsed .sidebar__help {
  justify-content: center;
  padding: 0.65rem;
}

.sidebar__divider {
  height: 1px;
  margin: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.12);
  flex-shrink: 0;
}

.sidebar__user {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin: 0.25rem 0.75rem 0.5rem;
  padding: 0.5rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
}

.sidebar--collapsed .sidebar__user {
  justify-content: center;
  padding: 0.5rem 0.4rem;
}

.sidebar__user-avatar {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 0.8rem;
  font-weight: 600;
  overflow: hidden;
}
.sidebar__user-avatar-img { width: 100%; height: 100%; object-fit: cover; display: block; }

.sidebar__user-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.sidebar__user-name {
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar__user-logout {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-family: var(--font-body);
  font-size: 0.72rem;
  color: var(--text-secondary, #9fb3d1);
  opacity: 0.85;
  cursor: pointer;
  text-decoration: underline;
}
.sidebar__user-logout:hover { opacity: 1; }

.sidebar__user-logout-icon {
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 1.05rem;
  cursor: pointer;
  opacity: 0.85;
}
.sidebar__user-logout-icon:hover { opacity: 1; }

.sidebar__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.75rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 6px;
  color: var(--text-primary);
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  transition: background 0.15s ease;
}

.sidebar__toggle:hover {
  background: rgba(255, 255, 255, 0.16);
}
</style>